import { TrendingUp, TrendingDown, Minus, Trophy, Flame } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { format, parseISO } from 'date-fns';
import { de as deLocale, enUS } from 'date-fns/locale';
import { StreakFreezeButton } from './StreakFreezeButton';

/**
 * Compact metric sparkline card used inside Dashboard module cards.
 * Renders a small inline SVG sparkline (no axes), the average value,
 * and a Δ% badge comparing against the previous same-length period.
 *
 * @param {object} metric - { values: [{date, value}], avg, delta_pct, delta_dir, prev_avg }
 * @param {string} label
 * @param {string} unit
 * @param {string} color - hex color for line/area
 * @param {(v:number)=>string} formatValue
 * @param {boolean} dense - smaller height if true
 * @param {() => void} onClick - optional click handler (renders as button)
 * @param {'workout'|'mood'} streakType - enables Streak Freeze button when present
 * @param {() => void} onFreezeApplied - callback after a freeze is consumed
 */
export function MetricSparkline({ metric, label, unit = '', color = '#22c55e', formatValue, dense = false, testid, onClick, streakType, onFreezeApplied }) {
    const { language } = useLanguage();

    if (!metric) return null;
    const values = metric.values || [];
    // numbers used for path scaling (treat null as gap)
    const nums = values.map((v) => (v.value == null ? null : Number(v.value)));
    const hasData = nums.some((v) => v != null && v > 0);
    const validNums = nums.filter((v) => v != null);
    const min = validNums.length ? Math.min(...validNums) : 0;
    const max = validNums.length ? Math.max(...validNums) : 1;
    const range = Math.max(0.0001, max - min);

    const w = 100;
    const h = dense ? 24 : 32;
    const stepX = nums.length > 1 ? w / (nums.length - 1) : w;

    // Build SVG path skipping null values (gaps)
    const segments = [];
    let cur = [];
    nums.forEach((v, i) => {
        if (v == null) {
            if (cur.length > 1) segments.push(cur);
            cur = [];
        } else {
            const x = i * stepX;
            const y = h - ((v - min) / range) * (h - 4) - 2;
            cur.push([x, y]);
        }
    });
    if (cur.length > 1) segments.push(cur);

    const linePath = segments
        .map((seg) => 'M ' + seg.map((p) => `${p[0].toFixed(2)} ${p[1].toFixed(2)}`).join(' L '))
        .join(' ');

    // Area path (filled): join last segment to bottom + first to bottom
    const areaPath = segments
        .map((seg) => {
            if (seg.length < 2) return '';
            const first = seg[0];
            const last = seg[seg.length - 1];
            return `M ${first[0].toFixed(2)} ${h} L ` + seg.map((p) => `${p[0].toFixed(2)} ${p[1].toFixed(2)}`).join(' L ') + ` L ${last[0].toFixed(2)} ${h} Z`;
        })
        .join(' ');

    const lastValid = nums[nums.length - 1];
    const showValue = lastValid != null ? lastValid : metric.avg;
    const formatted = formatValue ? formatValue(showValue) : (showValue ?? 0).toLocaleString();

    const delta = metric.delta_pct;
    const dir = metric.delta_dir;
    const hasSignal = hasData || (metric.prev_avg && metric.prev_avg > 0);
    const DeltaIcon = dir === 'up' ? TrendingUp : dir === 'down' ? TrendingDown : Minus;
    const deltaColor = dir === 'up' ? 'text-emerald-500' : dir === 'down' ? 'text-rose-500' : 'text-muted-foreground';

    const gradId = `spark-grad-${(label || 'm').replace(/\s+/g, '-')}-${color.replace('#', '')}`;

    const InnerWrapper = onClick ? 'button' : 'div';
    const innerProps = onClick
        ? { type: 'button', onClick, className: 'w-full text-left space-y-1 group rounded-lg -m-1 p-1 hover:bg-muted/40 active:bg-muted/60 transition-colors cursor-pointer', 'aria-label': `${label} — Details öffnen` }
        : { className: 'space-y-1' };

    return (
        <div className="space-y-1" data-testid={testid || 'metric-sparkline'}>
            <InnerWrapper {...innerProps}>
                <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] text-muted-foreground truncate group-hover:text-foreground transition-colors">{label}</span>
                    {delta != null && hasSignal && (
                        <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${deltaColor}`} data-testid={`${testid || 'metric'}-delta`}>
                            <DeltaIcon className="w-3 h-3" />
                            {delta > 0 ? '+' : ''}{delta}%
                        </span>
                    )}
                </div>
                <div className="flex items-end gap-2">
                    <span className="text-base font-bold tabular-nums leading-none">
                        {formatted}<span className="text-[10px] font-normal text-muted-foreground ml-0.5">{unit}</span>
                    </span>
                    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="flex-1 h-7 overflow-visible" aria-hidden="true">
                        <defs>
                            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                                <stop offset="100%" stopColor={color} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        {hasData ? (
                            <>
                                {areaPath && <path d={areaPath} fill={`url(#${gradId})`} />}
                                {linePath && <path d={linePath} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />}
                                {nums[nums.length - 1] != null && (
                                    <circle
                                        cx={(nums.length - 1) * stepX}
                                        cy={h - ((nums[nums.length - 1] - min) / range) * (h - 4) - 2}
                                        r="2"
                                        fill={color}
                                    />
                                )}
                            </>
                        ) : (
                            <line x1="0" y1={h / 2} x2={w} y2={h / 2} stroke="currentColor" strokeOpacity="0.15" strokeDasharray="2 3" />
                        )}
                    </svg>
                </div>
            </InnerWrapper>
            {(metric.streak > 0 || (metric.personal_best && metric.personal_best.value > 0) || (streakType && metric.freeze)) && (
                <div className="flex items-center gap-2 flex-wrap">
                    {metric.streak > 0 && (
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground/80" data-testid={`${testid || 'metric'}-streak`}>
                            <Flame className="w-2.5 h-2.5 text-orange-500 shrink-0" />
                            <span>
                                <span className="font-semibold text-foreground/70">{metric.streak}</span>
                                {' '}
                                {language === 'de'
                                    ? (metric.streak === 1 ? 'Tag-Streak' : 'Tage-Streak')
                                    : (metric.streak === 1 ? 'day streak' : 'day streak')}
                            </span>
                        </div>
                    )}
                    {streakType && metric.freeze && (
                        <StreakFreezeButton
                            freeze={metric.freeze}
                            streakType={streakType}
                            onApplied={onFreezeApplied}
                            testid={`${testid || 'metric'}-freeze`}
                        />
                    )}
                    {metric.personal_best && metric.personal_best.value > 0 && (() => {
                        let pbDate = null;
                        try { pbDate = parseISO(metric.personal_best.date); } catch (e) { /* ignore */ }
                        const pbVal = formatValue ? formatValue(metric.personal_best.value) : metric.personal_best.value.toLocaleString();
                        const dateStr = pbDate ? format(pbDate, 'd. MMM', { locale: language === 'de' ? deLocale : enUS }) : '';
                        return (
                            <div className="flex items-center gap-1 text-[10px] text-muted-foreground/80" data-testid={`${testid || 'metric'}-pb`}>
                                <Trophy className="w-2.5 h-2.5 text-amber-400 shrink-0" />
                                <span className="truncate">
                                    <span className="font-semibold text-foreground/70">{pbVal}{unit}</span>
                                    {dateStr && <span> · {dateStr}</span>}
                                </span>
                            </div>
                        );
                    })()}
                </div>
            )}
        </div>
    );
}
